import React, { useState, useRef, useEffect } from "react";
import { Outlet, useLocation, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Menu from "../components/Menu";
import Header from "./Header";
import { DashboardProvider } from "../context/DashboardContext";
import { useAuth } from "../hooks/useAuth";
import { AuthenticatedRequest, getSocketBaseUrl } from "../api";

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { io } from "socket.io-client";

const MainLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [maintenanceSettings, setMaintenanceSettings] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  // Global Socket Listener & Maintenance Logic
  useEffect(() => {
    if (currentUser) {
        const socketUrl = getSocketBaseUrl();
        const socket = io(socketUrl, {
            transports: ["websocket", "polling"] // Enforce transports to avoid some issues
        });
        socket.emit('register', currentUser.id);
        
        socket.on('notification', (data) => {
             const options = { position: "top-right", autoClose: 5000 };
             if (data.type === 'error') toast.error(data.message, options);
             else if (data.type === 'warning') toast.warn(data.message, options);
             else toast.info(data.message, options);
        });

        // Listen for internal settings updates
        socket.on('system_settings_updated', (newSettings) => {
             // If relevant keys changed, update local state
             if (newSettings.maintenance_mode || newSettings.maintenance_config) {
                 fetchMaintenanceStatus();
             }
        });

        return () => socket.disconnect();
    }
  }, [currentUser]);

  // Fetch initial status
  useEffect(() => {
      if (currentUser) fetchMaintenanceStatus();
  }, [currentUser]);

  const fetchMaintenanceStatus = async () => {
      try {
          const res = await AuthenticatedRequest.get("/admin/settings/maintenance-status");
          setMaintenanceSettings(res.data);
      } catch (e) {
          console.error("Failed to check maintenance status");
      }
  };

  // Check Route Access
  useEffect(() => {
     if (!maintenanceSettings || !currentUser) return;
     
     // Admins bypass everything
     if (currentUser.role === 'admin') return;

     if (maintenanceSettings.maintenance_mode) {
         const config = maintenanceSettings.config || { scope: 'full', modules: [] };
         
         if (config.scope === 'full') {
             // Handled by API 503 redirect mostly, but we can double check
             if (location.pathname !== "/dashboard/online/maintenance") {
                 navigate("/dashboard/online/maintenance");
             }
         } else if (config.scope === 'partial') {
             // Check frontend route mapping
             const currentPath = location.pathname;
             const blockedModules = config.modules || [];
             
             // Define Map: Route Prefix -> Module ID
             const ROUTE_MAP = {
                 "/dashboard/online/tiktok": "tiktok",
                 "/dashboard/online/shopee": "shopee",
                 "/dashboard/online/products": "product",
                 "/dashboard/online/inventory": "inventory",
                 "/dashboard/online/customers": "customer",
                 "/dashboard/online/location": "location",
                 "/dashboard/online/tools/deal-calculator": "deal",
                 "/dashboard/online/tools/compensation-order": "compensation",
             };

             for (const [prefix, modId] of Object.entries(ROUTE_MAP)) {
                 if (currentPath.startsWith(prefix) && blockedModules.includes(modId)) {
                     if (currentPath !== "/dashboard/online/maintenance") {
                         navigate("/dashboard/online/maintenance");
                     }
                     break;
                 }
             }
         }
     }
  }, [location.pathname, maintenanceSettings, currentUser, navigate]);


  const getPageTitle = (pathname) => {
    if (pathname.includes("/tiktok")) return "Kênh TikTok";
    if (pathname.includes("/shopee")) return "Kênh Shopee";
    if (pathname.includes("/product")) return "Phân tích sản phẩm";
    if (pathname.includes("/inventory")) return "Quản lý tồn kho";
    if (pathname.includes("/location")) return "Bản đồ phân bổ";
    if (pathname.includes("/customers")) return "Khách hàng 360";
    if (pathname.includes("/admin")) return "Quản trị hệ thống";
    if (pathname.includes("/tools/deal-calculator")) return "Công cụ tính Deal";
    if (pathname.includes("/tools/compensation-order")) return "Tạo Đơn Bù";
    return "Tổng quan";
  };

  const pageTitle = getPageTitle(location.pathname);

  return (
    <DashboardProvider>
      <Helmet>
        <title>{pageTitle} | E-commerce Dashboard</title>
      </Helmet>
      <ToastContainer />
      <div className="flex min-h-screen bg-[#fffdf5] dark:bg-slate-950 font-sans selection:bg-red-100 selection:text-red-900 transition-colors duration-300 relative">
         {/* Background Texture */}
         <div className="absolute inset-0 z-0 opacity-[0.03] bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] pointer-events-none"></div>
        {/* Mobile Backdrop */}
        {mobileMenuOpen && (
          <div 
            className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm lg:hidden animate-fade-in"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar Container */}
        <aside className={`
          fixed left-0 top-0 h-full w-64 lg:w-fit z-[100] 
          flex flex-col items-center py-6 pl-4 
          transition-transform duration-300 ease-spring
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          bg-transparent
        `}>
          <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border border-red-100 dark:border-red-900/30 shadow-glass rounded-3xl h-full flex flex-col p-2 relative transition-colors w-full">
            
            {/* Logo/Brand Area (Optional addition for mobile context) */}
            <div className="lg:hidden mb-6 px-4 pt-2">
                 <h2 className="text-xl font-bold font-display text-primary-600">Dashboard</h2>
            </div>

            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto lg:overflow-visible no-scrollbar">
                <Menu />
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 lg:ml-24 transition-all duration-300 min-w-0">
           {/* Mobile Header logic could go here or within Header itself */}
          <Header title={pageTitle} onMenuToggle={toggleMobileMenu} />

          <div className="p-4 md:p-8 max-w-[1600px] mx-auto min-h-screen animate-fade-in">
             <div className="mt-2">
                <Outlet />
             </div>
          </div>
        </main>
      </div>
    </DashboardProvider>
  );
};

export default MainLayout;
